import { API } from './utils';
import { getInitialLanguage } from './translations';

// Module-level mutable language state
let currentLanguage = getInitialLanguage();

export const setCurrentLanguage = (lang) => { currentLanguage = lang; };
export const getCurrentLanguage = () => currentLanguage;

// Voice settings cache
let voiceSettings = { enabled: true, volume: 1.0, speed: 0.85 };

// Fetch voice settings from server
export const fetchVoiceSettings = async () => {
  try {
    const response = await (await import('axios')).default.get(`${API}/voice-settings`);
    voiceSettings = {
      enabled: response.data.voice_enabled,
      volume: response.data.voice_volume,
      speed: response.data.voice_speed || 0.85
    };
  } catch (error) {
    console.error("Failed to fetch voice settings:", error);
  }
};

// Initialize voice settings on load
fetchVoiceSettings();

// Get time period for voice message selection
export const getTimePeriod = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
};

// Get greeting based on time of day (with language support)
export const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (currentLanguage === 'es') {
    if (hour >= 5 && hour < 12) return "Buenos días";
    if (hour >= 12 && hour < 17) return "Buenas tardes";
    if (hour >= 17 && hour < 21) return "Buenas tardes";
    return "Buenas noches";
  } else {
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Good night";
  }
};

// Track current playing audio to prevent overlap
let currentAudio = null;
let audioPlaying = false;
let audioQueue = [];
let isProcessingQueue = false;

// Stop all audio immediately
export const stopAllAudio = () => {
  audioQueue = [];
  isProcessingQueue = false;
  
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio.src = '';
      currentAudio.onended = null;
      currentAudio.onerror = null;
    } catch (e) {
      console.log("Error stopping audio:", e);
    }
    currentAudio = null;
  }
  audioPlaying = false;
  
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

// Process audio queue one at a time
const processAudioQueue = () => {
  if (isProcessingQueue || audioQueue.length === 0) return;
  
  isProcessingQueue = true;
  const { url, volume, onEnd, fallbackText } = audioQueue.shift();
  
  const audio = new Audio(url);
  audio.volume = volume;
  currentAudio = audio;
  audioPlaying = true;
  
  const cleanup = () => {
    audioPlaying = false;
    currentAudio = null;
    isProcessingQueue = false;
    if (onEnd) onEnd();
    setTimeout(processAudioQueue, 200);
  };
  
  audio.onended = cleanup;
  
  audio.onerror = () => {
    console.log("Audio error, trying fallback...");
    if (fallbackText && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(fallbackText);
      utterance.volume = volume;
      utterance.rate = 0.9;
      utterance.lang = currentLanguage === 'es' ? 'es-MX' : 'en-US';
      utterance.onend = cleanup;
      utterance.onerror = cleanup;
      window.speechSynthesis.speak(utterance);
    } else {
      cleanup();
    }
  };
  
  audio.play().catch(() => {
    // stopAllAudio() may have already nulled the handler — guard so an
    // offline voice fetch can't throw "audio.onerror is not a function"
    if (typeof audio.onerror === 'function') {
      audio.onerror();
    }
  });
};

// Audio player for voice messages (works on Fully Kiosk)
export const playVoiceMessage = (messageId, onEnd = null) => {
  if (!voiceSettings.enabled) {
    console.log("Voice is disabled");
    if (onEnd) onEnd();
    return;
  }
  
  stopAllAudio();
  
  const fallbackMessages = currentLanguage === 'es' ? {
    "enter_employee_number": "Por favor ingrese su número de empleado.",
    "register_welcome": "Bienvenido a Hodler Inn. Si es su primera vez aquí, por favor registre su número de empleado y nombre.",
    "checkin_welcome_morning": "Buenos días. Bienvenido de nuevo a Hodler Inn.",
    "checkin_welcome_afternoon": "Buenas tardes. Bienvenido de nuevo a Hodler Inn.",
    "checkin_welcome_evening": "Buenas tardes. Bienvenido de nuevo a Hodler Inn.",
    "checkin_welcome_night": "Buenas noches. Bienvenido de nuevo a Hodler Inn.",
    "checkin_complete": "Que descanse bien.",
    "checkout_morning": "Buenos días! Gracias por hospedarse en Hodler Inn. Buen viaje.",
    "checkout_afternoon": "Buenas tardes! Gracias por hospedarse en Hodler Inn. Buen viaje.",
    "checkout_evening": "Buenas tardes! Gracias por hospedarse en Hodler Inn. Buen viaje.",
    "checkout_night": "Buenas noches! Gracias por hospedarse en Hodler Inn. Buen viaje.",
    "signature_reminder": "Por favor firme su nombre completo de forma legible.",
    "room_reminder": "Por favor seleccione el número de habitación de la llave en el escritorio.",
    "checkout_found": "Reserva encontrada. Por favor ingrese su hora de servicio y presione Completar salida.",
    "checkout_enter_room": "Por favor ingrese su número de habitación para hacer el check out.",
    "yellow_card_instructions": "Su número de empleado no se encontró en nuestro sistema. Por favor use la tarjeta amarilla en el escritorio. Escriba su número de empleado, su nombre completo y la hora en la tarjeta. El administrador lo registrará. Gracias."
  } : {
    "enter_employee_number": "Please enter your employee number.",
    "register_welcome": "Welcome to Hodler Inn. If you are first time here, please register your employee number and name, then go to check in.",
    "checkin_welcome_morning": "Good morning. Welcome back to Hodler Inn.",
    "checkin_welcome_afternoon": "Good afternoon. Welcome back to Hodler Inn.",
    "checkin_welcome_evening": "Good evening. Welcome back to Hodler Inn.",
    "checkin_welcome_night": "Good night. Welcome back to Hodler Inn.",
    "checkin_complete": "Have a good rest.",
    "checkout_morning": "Good morning! Thank you for staying at Hodler Inn. Have a safe journey.",
    "checkout_afternoon": "Good afternoon! Thank you for staying at Hodler Inn. Have a safe journey.",
    "checkout_evening": "Good evening! Thank you for staying at Hodler Inn. Have a safe journey.",
    "checkout_night": "Good night! Thank you for staying at Hodler Inn. Have a safe journey.",
    "signature_reminder": "Please sign your full name legibly.",
    "room_reminder": "Please select the room number from key on desk.",
    "checkout_found": "Booking found. Please enter your on duty time and press Complete check out.",
    "checkout_enter_room": "Please enter your room number to check out.",
    "yellow_card_instructions": "Your employee number was not found in our system. Please use the yellow card on the desk. Write your employee number, your full name, and the time on the card. The admin will check you in. Thank you."
  };
  
  audioQueue.push({
    url: `${API}/voice/${messageId}?lang=${currentLanguage}`,
    volume: voiceSettings.volume,
    onEnd: onEnd,
    fallbackText: fallbackMessages[messageId]
  });
  
  setTimeout(processAudioQueue, 100);
};

// Play personalized welcome with name (using dynamic audio generation)
export const playWelcomeWithName = (name, isNewEmployee = false) => {
  if (!voiceSettings.enabled) return;
  
  if (!name || name === 'undefined' || name.trim() === '') {
    console.log("playWelcomeWithName: Invalid name, skipping voice");
    return;
  }
  
  stopAllAudio();
  
  const messageType = isNewEmployee ? "checkin_new" : "checkin";
  const encodedName = encodeURIComponent(name);
  const greeting = encodeURIComponent(getTimeBasedGreeting());
  const greetingText = getTimeBasedGreeting();
  const fallbackMessage = currentLanguage === 'es' 
    ? `${greetingText}, ${name}. Bienvenido a Hodler Inn. Por favor ingrese el número de habitación, la hora, firme su nombre y haga clic en Completar Registro.`
    : `${greetingText}, ${name}. Welcome back to Hodler Inn. Please enter room number, time, sign your name, and click Complete Check-In.`;
  
  audioQueue.push({
    url: `${API}/voice-dynamic/${messageType}/${encodedName}?greeting=${greeting}&lang=${currentLanguage}`,
    volume: voiceSettings.volume,
    onEnd: null,
    fallbackText: fallbackMessage
  });
  
  setTimeout(processAudioQueue, 100);
};

// Play checkout found with employee name (using dynamic audio generation)
export const playCheckoutFoundWithName = (name) => {
  if (!voiceSettings.enabled) return;
  
  if (!name || name === 'undefined' || name.trim() === '') {
    console.log("playCheckoutFoundWithName: Invalid name, skipping voice");
    return;
  }
  
  stopAllAudio();
  
  const encodedName = encodeURIComponent(name);
  const fallbackMessage = currentLanguage === 'es'
    ? `Reserva encontrada para ${name}. Por favor ingrese su hora de servicio y presione Completar salida.`
    : `Booking found for ${name}. Please enter your on duty time and press Complete check out.`;
  
  audioQueue.push({
    url: `${API}/voice-dynamic/checkout_found/${encodedName}?lang=${currentLanguage}`,
    volume: voiceSettings.volume,
    onEnd: null,
    fallbackText: fallbackMessage
  });
  
  setTimeout(processAudioQueue, 100);
};

// Legacy speakMessage function (now uses audio files)
export const speakMessage = (message, rate = null) => {
  const speechRate = rate !== null ? rate : voiceSettings.speed;
  
  const messageMap = {
    "Please sign your full name legibly": "signature_reminder",
    "Please select the room number from key on desk": "room_reminder",
    "Booking found": "checkout_found"
  };
  
  for (const [key, id] of Object.entries(messageMap)) {
    if (message.includes(key)) {
      playVoiceMessage(id);
      return;
    }
  }
  
  if (!voiceSettings.enabled) return;
  
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(message);
    utterance.rate = speechRate;
    utterance.volume = voiceSettings.volume;
    utterance.lang = currentLanguage === 'es' ? 'es-MX' : 'en-US';
    window.speechSynthesis.speak(utterance);
  }
};
